// ASK-1 + app tour entry — Ask Firefly on the expanded Activity dock panel.
// PA-1a — Personal Assistant mode toggle + thread shell.
// T-29 — Explore Firefly catalog chip + panel.

import { forwardRef, useEffect } from "react";
import { FireflyAskMicButton } from "@/components/activity/firefly/FireflyAskMicButton";
import {
  useActivityDockAskFireflyController,
  type ActivityDockAskFireflyHandle,
  type ActivityDockAskFireflyProps,
  type FireflyAskHighlightRequest,
} from "@/components/activity/firefly/useActivityDockAskFireflyController";
import { PersonalAssistantThreadShell } from "@/components/activity/personalAssistant/PersonalAssistantThreadShell";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { FIREFLY_ASK_INPUT_ATTR } from "@/lib/firefly/fireflyPttGating";
import { AskFireflyResult } from "./ActivityDockAskFireflyResult";
import { ActivityDockFireflyCatalog } from "./ActivityDockFireflyCatalog";

export type { ActivityDockAskFireflyHandle, ActivityDockAskFireflyProps, FireflyAskHighlightRequest };

export const ActivityDockAskFirefly = forwardRef<
  ActivityDockAskFireflyHandle,
  ActivityDockAskFireflyProps
>(function ActivityDockAskFirefly(props, ref) {
  const presentation = props.presentation ?? "card";
  const {
    assistLevel,
    auth,
    appendDictation,
    busy,
    canShowPaToggle,
    isPaModeActive,
    assistMode,
    onPaToggleChange,
    currentMode,
    disabled,
    expanded,
    getToken,
    inputId,
    pttListening,
    query,
    scopedOrganizationId,
    setExpanded,
    setPttListening,
    setQuery,
    submitQuery,
    openCatalog,
    startTourFromCatalog,
    closeCatalog,
    tabPttListening,
    textareaRef,
    uiResolution,
    onNavigate,
    onStartCompanionTour,
    highlightWithCallout,
    exitPaMode,
    onNavigateToMode,
  } = useActivityDockAskFireflyController(props, ref);

  useEffect(() => {
    if (presentation === "pane-ask") {
      exitPaMode();
      setExpanded(true);
    } else if (presentation === "pane-pa") {
      onPaToggleChange(true);
      setExpanded(true);
    }
  }, [exitPaMode, onPaToggleChange, presentation, setExpanded]);

  if (assistLevel === "off") return null;

  const showPttListening = pttListening || tabPttListening;
  const pane = presentation === "pane-ask" || presentation === "pane-pa";
  const showPaBody = presentation === "pane-pa" || (isPaModeActive && expanded);
  const showAskBody = presentation === "pane-ask" || (expanded && !isPaModeActive);
  const sectionTone =
    presentation === "pane-pa" || isPaModeActive
      ? "border-indigo-400/70 bg-indigo-50/60 dark:border-indigo-500/40 dark:bg-indigo-950/25"
      : expanded || pane
        ? "border-emerald-400/70 bg-emerald-50/60 dark:border-emerald-500/40 dark:bg-emerald-950/25"
        : "border-gray-200 bg-gray-50/80 dark:border-zinc-700 dark:bg-zinc-800/40";

  return (
    <section
      {...{ [FIREFLY_TARGET_ATTR]: "ask-firefly" }}
      className={[
        pane ? "rounded-xl border p-2 transition-colors" : "mb-3 rounded-xl border p-2.5 transition-colors",
        sectionTone,
      ].join(" ")}
      aria-label={presentation === "pane-pa" || isPaModeActive ? "Personal Assistant" : "Ask Firefly"}
    >
      {pane ? null : (
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3
            className={
              isPaModeActive
                ? "text-xs font-semibold text-indigo-800 dark:text-indigo-200"
                : "text-xs font-semibold text-emerald-800 dark:text-emerald-200"
            }
          >
            {isPaModeActive ? "Personal Assistant" : "Ask Firefly"}
          </h3>
          <p className="text-[11px] text-gray-600 dark:text-zinc-400">
            {isPaModeActive
              ? "Multi-turn assistant — confirm actions before they run."
              : 'How do I…? Type or hold mic — try "lead capture tour"'}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {canShowPaToggle ? (
            <label className="inline-flex max-w-[11rem] items-center gap-1.5 text-[10px] font-medium text-indigo-800 dark:text-indigo-200">
              <input
                type="checkbox"
                checked={assistMode === "personal_assistant"}
                onChange={(e) => onPaToggleChange(e.target.checked)}
                className="h-3.5 w-3.5 accent-indigo-600"
              />
              <span className="text-right leading-tight">Personal Assistant mode</span>
            </label>
          ) : null}
          <button
            type="button"
            className="text-[11px] font-medium text-emerald-700 hover:underline dark:text-emerald-300"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded || isPaModeActive}
          >
            {expanded || isPaModeActive ? "Hide" : "Ask"}
          </button>
        </div>
      </div>
      )}

      {showPaBody ? (
        <PersonalAssistantThreadShell
          disabled={disabled}
          currentMode={currentMode}
          scopedOrganizationId={scopedOrganizationId}
          onStartTour={props.onStartTour}
          onNavigateToMode={onNavigateToMode}
          onExitPaMode={exitPaMode}
        />
      ) : showAskBody ? (
        <div className="mt-2 space-y-2">
          {uiResolution.kind !== "catalog" ? (
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "firefly-catalog-chip" }}
              onClick={() => openCatalog("chip")}
              disabled={disabled || busy}
              className="rounded-full border border-emerald-300/80 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-700 dark:bg-zinc-900 dark:text-emerald-200 dark:hover:bg-emerald-950/40"
            >
              Show me what Firefly can do
            </button>
          ) : null}

          {uiResolution.kind === "catalog" ? (
            <ActivityDockFireflyCatalog
              entries={uiResolution.entries}
              onStartTour={startTourFromCatalog}
              onClose={closeCatalog}
            />
          ) : (
            <>
              <label htmlFor={inputId} className="sr-only">
                Ask Firefly a question
              </label>
              <textarea
                ref={textareaRef}
                id={inputId}
                {...{ [FIREFLY_ASK_INPUT_ATTR]: "true" }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitQuery();
                  }
                }}
                disabled={disabled || busy}
                rows={2}
                placeholder='Type or hold mic — e.g. "lead capture tour"'
                className={[
                  "w-full resize-none rounded-lg border bg-white px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:opacity-50 dark:bg-zinc-900 dark:text-zinc-100",
                  showPttListening
                    ? "border-emerald-500 ring-2 ring-emerald-300 dark:border-emerald-400 dark:ring-emerald-700"
                    : "border-gray-300 focus:ring-2 focus:ring-emerald-300 dark:border-zinc-600 dark:focus:ring-emerald-700",
                ].join(" ")}
              />
              {showPttListening ? (
                <p className="text-[11px] text-emerald-700 dark:text-emerald-300" role="status">
                  Listening… release to ask
                </p>
              ) : null}
              <div className="flex items-end justify-between gap-2">
                <FireflyAskMicButton
                  onAppend={appendDictation}
                  onRelease={() =>
                    submitQuery({ inputModality: "voice", pttTrigger: "ask_mic" })
                  }
                  onListeningChange={setPttListening}
                  disabled={disabled || busy || tabPttListening}
                />
                <button
                  type="button"
                  onClick={() => submitQuery()}
                  disabled={disabled || busy || !query.trim()}
                  className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                >
                  Go
                </button>
              </div>

              {uiResolution.kind === "tour" ? (
                <p className="text-xs text-emerald-800 dark:text-emerald-200" role="status">
                  {uiResolution.message}
                </p>
              ) : null}
              {uiResolution.kind === "ask" && currentMode != null ? (
                <AskFireflyResult
                  queryText={query}
                  resolution={uiResolution.resolution}
                  currentMode={currentMode}
                  auth={auth}
                  scopedOrganizationId={scopedOrganizationId}
                  getToken={getToken}
                  onNavigate={onNavigate}
                  onHighlightHere={highlightWithCallout}
                  onStartCompanionTour={onStartCompanionTour}
                />
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
});
